/**
 * Publier d'un coup les brouillons issus d'une version de tarif (lot C2.2).
 *
 * Fonctions PURES, sans React : quels brouillons se publient, ce qu'on annonce AVANT le geste irréversible, et le
 * bilan. Règles posées avec Benjamin (2026-10-12) : **une seule confirmation « IMMUABLE »**, un bilan par gamme, et
 * **jamais un brouillon dont la gamme a changé depuis sa création** (le publier effacerait la correction faite entre-temps).
 *
 * Chaque gamme se publie par SA route (`publishMaintenanceVersion`), l'une après l'autre : un refus sur une gamme
 * n'arrête pas les autres, et ce qui est publié reste publié. L'effet sur les machines est dit par la MÊME règle que
 * pour une gamme seule (`effetDePublication`).
 */
import type { BrouillonEnAttenteTarif } from '../api/client';
import { dateFr } from './gammes';
import { effetDePublication } from './gammes-versions';

const accord = (n: number, singulier: string, pluriel: string): string => `${n} ${n > 1 ? pluriel : singulier}`;

/** Ceux qu'on peut publier : jamais un brouillon « périmé » (la version en vigueur de sa gamme n'est plus sa source). */
export const publiables = (brouillons: readonly BrouillonEnAttenteTarif[]): BrouillonEnAttenteTarif[] =>
  brouillons.filter((b) => !b.perimee);

/** Ceux qu'on écarte, à relire à part. */
export const ecartes = (brouillons: readonly BrouillonEnAttenteTarif[]): BrouillonEnAttenteTarif[] =>
  brouillons.filter((b) => b.perimee);

export interface RepartitionParEffet {
  readonly maintenant: readonly BrouillonEnAttenteTarif[];
  readonly plusTard: readonly BrouillonEnAttenteTarif[];
  readonly sansEffet: readonly BrouillonEnAttenteTarif[];
}

/** Ce que la publication change pour chaque gamme : tout de suite, plus tard, ou jamais (date trop ancienne). */
export function repartirParEffet(brouillons: readonly BrouillonEnAttenteTarif[], aujourdhui: string): RepartitionParEffet {
  const parEffet = (effet: 'MAINTENANT' | 'PLUS_TARD' | 'SANS_EFFET') =>
    brouillons.filter((b) => effetDePublication(b.validFrom, b.enVigueur, aujourdhui) === effet);
  return { maintenant: parEffet('MAINTENANT'), plusTard: parEffet('PLUS_TARD'), sansEffet: parEffet('SANS_EFFET') };
}

/**
 * Le texte de l'unique confirmation. On y dit : « IMMUABLE », combien de gammes, ce que cela change pour les machines
 * (tout de suite, plus tard, jamais), ce qui est écarté, et comment revenir en arrière.
 */
export function confirmationPublicationGroupee(brouillons: readonly BrouillonEnAttenteTarif[], aujourdhui: string): string {
  const aPublier = publiables(brouillons);
  const aEcarter = ecartes(brouillons);
  const { maintenant, plusTard, sansEffet } = repartirParEffet(aPublier, aujourdhui);

  const lignes: string[] = [
    `Publier ${accord(aPublier.length, 'brouillon', 'brouillons')} ?`,
    '',
    'Une version publiée est IMMUABLE : son contenu, ses prix et sa date d’effet ne changeront plus jamais. ' +
      'Toute correction ultérieure créera une nouvelle version.',
    '',
  ];

  if (maintenant.length > 0) {
    lignes.push(
      `• ${accord(maintenant.length, 'prendra effet tout de suite', 'prendront effet tout de suite')} : ` +
        'chacune remplacera la version en vigueur de sa gamme pour les machines rattachées.',
    );
  }
  if (plusTard.length > 0) {
    const sansVersion = plusTard.filter((b) => b.enVigueur === null);
    lignes.push(
      `• ${accord(plusTard.length, 'ne s’appliquera qu’à une date future', 'ne s’appliqueront qu’à une date future')} ` +
        `(${plusTard.map((b) => `${b.code}, le ${dateFr(b.validFrom)}`).join(' ; ')}) : d’ici là, la version en vigueur de chaque gamme reste en place.` +
        (sansVersion.length > 0
          ? ` Sans version en vigueur d’ici là : ${sansVersion.map((b) => b.code).join(', ')}.`
          : ''),
    );
  }
  if (sansEffet.length > 0) {
    lignes.push(
      `• ${accord(sansEffet.length, 'ne deviendra PAS la version en vigueur', 'ne deviendront PAS la version en vigueur')} ` +
        `(${sansEffet.map((b) => b.code).join(', ')}) : ` +
        (sansEffet.length > 1
          ? 'la version en vigueur de chacune de ces gammes a une date d’effet plus récente et reste en place.'
          : 'la version en vigueur de cette gamme a une date d’effet plus récente et reste en place.'),
    );
  }
  if (aEcarter.length > 0) {
    lignes.push(
      '',
      `${accord(aEcarter.length, 'brouillon est écarté', 'brouillons sont écartés')} (${aEcarter.map((b) => b.code).join(', ')}) : ` +
        (aEcarter.length > 1
          ? 'leur gamme a changé depuis leur création, à relire à part.'
          : 'sa gamme a changé depuis sa création, à relire à part.'),
    );
  }
  lignes.push(
    '',
    'Pour revenir en arrière sur une gamme : archivez sa nouvelle version, la précédente reprendra le relais.',
  );
  return lignes.join('\n');
}

export interface ResultatPublication {
  readonly profileId: string;
  readonly code: string;
  readonly versionNumber: number;
  /** `null` : publiée. Sinon le message de refus de l'API. */
  readonly erreur: string | null;
}

/**
 * Publie les brouillons UN PAR UN. Un refus est consigné et n'arrête pas les suivants : chaque publication est
 * atomique côté serveur, ce qui est publié reste publié.
 */
export async function publierEnSerie(
  brouillons: readonly BrouillonEnAttenteTarif[],
  publier: (brouillon: BrouillonEnAttenteTarif) => Promise<unknown>,
  progression?: (faits: number, total: number) => void,
): Promise<ResultatPublication[]> {
  const resultats: ResultatPublication[] = [];
  for (const b of brouillons) {
    let erreur: string | null = null;
    try {
      await publier(b);
    } catch (e) {
      erreur = e instanceof Error && e.message ? e.message : 'Publication impossible.';
    }
    resultats.push({ profileId: b.profileId, code: b.code, versionNumber: b.versionNumber, erreur });
    progression?.(resultats.length, brouillons.length);
  }
  return resultats;
}

/** Le bilan : ce qui est publié, et ce qui a été refusé avec sa raison. */
export function bilanPublication(resultats: readonly ResultatPublication[]): {
  readonly succes: string | null;
  readonly refus: string | null;
} {
  const reussies = resultats.filter((r) => r.erreur === null);
  const refusees = resultats.filter((r) => r.erreur !== null);
  return {
    succes: reussies.length > 0 ? `${accord(reussies.length, 'version publiée', 'versions publiées')}.` : null,
    refus:
      refusees.length > 0
        ? `${accord(refusees.length, 'gamme refusée', 'gammes refusées')} : ` +
          refusees.map((r) => `${r.code} v${r.versionNumber} (${r.erreur})`).join(' ; ')
        : null,
  };
}
